import type { TonyteActivity } from '../types';
import { normalizeCityKey } from './cities';
import { isEventAvailableForDiscovery } from './eventAvailability';
import { parseActivityDate } from './activityDates';

// The carousel is a curated highlight, not a listing — cap it so a run of
// admin picks can never turn it into a second feed.
export const MAX_WEEKLY_FEATURED = 10;

const UNPRIORITISED = Number.MAX_SAFE_INTEGER;

function parseMs(value: string | undefined): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

// True only while the admin flag is on AND `now` is inside the featured
// window. A flag with no (or unreadable) window is treated as NOT featured
// on purpose: an open-ended feature would never expire on its own, which is
// exactly what the weekly window exists to prevent.
export function isInWeeklyFeaturedWindow(activity: TonyteActivity, now: Date = new Date()): boolean {
  if (activity.isWeeklyFeatured !== true) return false;
  const start = parseMs(activity.featuredStartAt);
  const end = parseMs(activity.featuredEndAt);
  if (start === null || end === null) return false;
  const nowMs = now.getTime();
  return nowMs >= start && nowMs <= end;
}

export function isWeeklyFeaturedActive(activity: TonyteActivity, now: Date = new Date()): boolean {
  return isInWeeklyFeaturedWindow(activity, now) && isEventAvailableForDiscovery(activity, now);
}

function compareFeatured(a: TonyteActivity, b: TonyteActivity): number {
  const priorityA = typeof a.featuredPriority === 'number' ? a.featuredPriority : UNPRIORITISED;
  const priorityB = typeof b.featuredPriority === 'number' ? b.featuredPriority : UNPRIORITISED;
  if (priorityA !== priorityB) return priorityA - priorityB;
  const startA = parseActivityDate(a).startAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const startB = parseActivityDate(b).startAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
  return startA - startB || a.title.localeCompare(b.title);
}

// Featured events for the selected city, ordered by admin priority. Works on
// the already-fetched feed pool, so it adds no Firestore reads. Deliberately
// independent of the feed's date/chip filters: it is a week-scoped surface,
// and hiding a partner's featured event because the user tapped "Tomorrow"
// would defeat the point of featuring it.
export function selectWeeklyFeatured(
  activities: TonyteActivity[],
  options: { now?: Date; cityKey?: string | null; limit?: number } = {},
): TonyteActivity[] {
  const now = options.now ?? new Date();
  const cityKey = options.cityKey ? normalizeCityKey(options.cityKey) : '';
  const seen = new Set<string>();
  return activities
    .filter(activity => {
      if (!isWeeklyFeaturedActive(activity, now)) return false;
      if (cityKey && normalizeCityKey(activity.cityKey || activity.city) !== cityKey) return false;
      if (seen.has(activity.id)) return false;
      seen.add(activity.id);
      return true;
    })
    .sort(compareFeatured)
    .slice(0, options.limit ?? MAX_WEEKLY_FEATURED);
}

// Monday 00:00:00.000 -> Sunday 23:59:59.999 in the device's local time.
// `weekOffset` 0 = the current week, 1 = next week.
export function getWeekRange(now: Date = new Date(), weekOffset = 0): { start: Date; end: Date } {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const daysSinceMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - daysSinceMonday + weekOffset * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

// The Partner Dashboard stamps createdByRole with the creator's auth role at
// creation time. This is the cheap synchronous signal; the admin editor also
// verifies against partnerProfiles/{uid} (see weeklyFeaturedActions.ts) for
// events that predate the field.
export function hasPartnerCreatorRole(activity: TonyteActivity): boolean {
  return activity.createdByRole === 'partner';
}

// The account that owns an event, in the same precedence the Partner
// Dashboard's ownership queries use.
export function getEventOwnerUid(activity: TonyteActivity): string | null {
  const candidates = [activity.ownerUid, activity.createdBy, activity.hostId];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate && candidate !== 'system') return candidate;
  }
  return null;
}
